import Modal from '@components/Modal'
import { formatInstant, unixToDate } from '@lib/datetime'
import { participantShare } from '@orbit/money'
import { getShortName } from '@/utils/userNames'
import { GetExpenseResponse, GetUserType } from '@orbit/api-types'
import React, { useState } from 'react'
import { formatAmount, getInvolvementText, getUserInvolvement } from '../balancesFormat'
import {
    ActionButton,
    Amount,
    AmountRow,
    AuditInfo,
    AuditLine,
    CurrencyCode,
    DateRow,
    ExpenseName,
    FooterActions,
    HeaderRow,
    ImageOverlay,
    InvolvementRow,
    PaidByLabel,
    PaidByName,
    PaidByRow,
    SectionTitle,
    SplitAmount,
    SplitList,
    SplitName,
    SplitRow,
    SplitSection,
    SplitTypeBadge,
    Thumbnail,
    ThumbnailGrid,
} from './ExpenseDetailsModal.styles'
import { backgroundOrange } from '@/theme/colors'
import { Avatar } from '@components/Avatar'

interface ExpenseDetailsModalProps {
    isOpen: boolean
    onClose: () => void
    onEdit?: () => void
    onDelete?: () => void
    expense: GetExpenseResponse | null
    members: GetUserType[]
    currentUserId: number | undefined
    fileUrlById: Map<string, string>
    showCurrencyCode: boolean
}

const formatDate = (unixSeconds: number): string => formatInstant(unixToDate(unixSeconds), 'dateLong')

const splitTypeLabel = (splitType: string): string | null => {
    if (splitType === 'Percent') return '% split'
    if (splitType === 'Value') return '$ split'
    return null
}

const ExpenseDetailsModal: React.FC<ExpenseDetailsModalProps> = ({
    isOpen,
    onClose,
    onEdit,
    onDelete,
    expense,
    members,
    currentUserId,
    fileUrlById,
    showCurrencyCode,
}) => {
    const [overlayUrl, setOverlayUrl] = useState<string | null>(null)

    if (!expense) return null

    const me = members.find(m => m.id === currentUserId)
    const payer = members.find(m => m.id === expense.paidByUserId)
    const createdBy = members.find(m => m.id === expense.createdByUserId)
    const lastUpdatedBy = members.find(m => m.id === expense.lastUpdatedByUserId)

    const wasEdited = expense.lastUpdatedAt !== expense.expenseDate
    const involvement = getUserInvolvement(expense, me)

    const fileUrls = (expense.fileIds ?? [])
        .map(id => fileUrlById.get(id))
        .filter((url): url is string => !!url)

    const splitBadge = splitTypeLabel(expense.splitType)

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Expense Details"
            size="medium"
            backgroundColor={backgroundOrange}
            footer={
                onEdit || onDelete ? (
                    <FooterActions>
                        {onDelete && (
                            <ActionButton type="button" variant="danger" onClick={onDelete}>
                                Delete
                            </ActionButton>
                        )}
                        {onEdit && (
                            <ActionButton type="button" variant="primary" onClick={onEdit}>
                                Edit
                            </ActionButton>
                        )}
                    </FooterActions>
                ) : null
            }
        >
            <HeaderRow>
                <ExpenseName>{expense.name}</ExpenseName>
                <PaidByRow>
                    <PaidByLabel>Paid by</PaidByLabel>
                    <Avatar size="md" user={payer} />
                    <PaidByName>{getShortName(payer)}</PaidByName>
                </PaidByRow>
            </HeaderRow>

            <AmountRow>
                <Amount>{formatAmount(expense.amount, expense.currencyCode)}</Amount>
                {showCurrencyCode && (
                    <CurrencyCode>{expense.currencyCode.toUpperCase()}</CurrencyCode>
                )}
            </AmountRow>

            <DateRow>{formatDate(expense.expenseDate)}</DateRow>

            {me && involvement.type !== 'none' && (
                <InvolvementRow $type={involvement.type}>
                    {getInvolvementText(expense, me)}
                </InvolvementRow>
            )}

            <SplitSection>
                <SectionTitle>
                    Split between {expense.participants.length}
                    {splitBadge && <SplitTypeBadge>{splitBadge}</SplitTypeBadge>}
                </SectionTitle>
                <SplitList>
                    {expense.participants.map(participant => {
                        const member = members.find(m => m.id === participant.userId)
                        const shareCents = participantShare(expense, participant)
                        return (
                            <SplitRow key={participant.userId}>
                                <Avatar size="md" user={member} />
                                <SplitName>
                                    {participant.userId === currentUserId ? 'You' : getShortName(member)}
                                </SplitName>
                                <SplitAmount>
                                    {formatAmount(shareCents, expense.currencyCode)}
                                </SplitAmount>
                            </SplitRow>
                        )
                    })}
                </SplitList>
            </SplitSection>

            {fileUrls.length > 0 && (
                <ThumbnailGrid>
                    {fileUrls.map(url => (
                        <Thumbnail key={url} onClick={() => setOverlayUrl(url)}>
                            <img src={url} alt="Receipt" />
                        </Thumbnail>
                    ))}
                </ThumbnailGrid>
            )}

            <AuditInfo>
                <AuditLine>
                    Added by <strong>{getShortName(createdBy)}</strong>
                </AuditLine>
                {wasEdited && (
                    <AuditLine>
                        Last updated by <strong>{getShortName(lastUpdatedBy)}</strong> on{' '}
                        {formatDate(expense.lastUpdatedAt)}
                    </AuditLine>
                )}
            </AuditInfo>

            {overlayUrl && (
                <ImageOverlay onClick={() => setOverlayUrl(null)}>
                    <img src={overlayUrl} alt="Receipt full size" />
                </ImageOverlay>
            )}
        </Modal>
    )
}

export default ExpenseDetailsModal
